import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BlockMath, InlineMath } from 'react-katex';
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SectionWrapper from '@/components/SectionWrapper';
import FormulaCard from '@/components/FormulaCard';
import PerUnitBaseCalc from '@/calculators/PerUnitBaseCalc';
import BaseChangeCalc from '@/calculators/BaseChangeCalc';
import NetworkBuilder from '@/calculators/NetworkBuilder';

// ─── Section 1: Why It Matters ───
const WhyItMatters = () => (
  <SectionWrapper number="1" title="Why It Matters" id="why-it-matters">
    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-5 mb-5">
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">Real Utility Scenario</p>
      <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm">
        A 30 MVA, 138/13.8 kV transformer feeds a 5 km distribution feeder. The transformer leakage reactance
        is 17.14 Ω referred to the 138 kV side — but only 0.17 Ω referred to the 13.8 kV side.
        The feeder line has X = 2.0 Ω at 13.8 kV. Without per-unit, you must carefully track which side you're on
        and carry the turns ratio through every calculation step.
      </p>
    </div>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
      Per-unit analysis solves this by normalizing every quantity — voltage, current, impedance, power — to a
      dimensionless ratio of its actual value to a chosen base value. Once everything is in per-unit, transformer
      turns ratios disappear from the network equations, and a 10% transformer looks like{' '}
      <InlineMath math="j0.10 \text{ pu}" /> regardless of which voltage side you're standing on.
    </p>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
      Per-unit is the universal language of power systems fault analysis, protection coordination, and
      load flow studies. Every relay setting, every fault current calculation, and every short-circuit
      study you will encounter in distribution engineering uses per-unit values.
    </p>
  </SectionWrapper>
);

// ─── Section 2: Concept Visual ───
const ConceptVisual = () => (
  <SectionWrapper number="2" title="Concept Visual" id="concept-visual" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
      The diagram below shows the same two-bus system in actual ohms (left) and in per-unit (right).
      Choosing the correct voltage base on each side of the transformer is what makes the impedances on
      both sides directly comparable.
    </p>
    <div className="overflow-x-auto">
      <svg viewBox="0 0 680 240" className="w-full max-w-2xl mx-auto" style={{ minWidth: 320 }}>
        {/* Background panels */}
        <rect x="10" y="10" width="295" height="220" rx="10" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="6 3" />
        <rect x="375" y="10" width="295" height="220" rx="10" fill="none" stroke="#0d9488" strokeWidth="1.5" />

        {/* Labels */}
        <text x="25" y="32" fontSize="11" fill="#94a3b8" fontFamily="IBM Plex Mono" fontWeight="600">ACTUAL SYSTEM</text>
        <text x="390" y="32" fontSize="11" fill="#0d9488" fontFamily="IBM Plex Mono" fontWeight="600">PER-UNIT SYSTEM</text>

        {/* Actual system buses */}
        <rect x="25" y="90" width="12" height="80" fill="#0B172A" rx="2" />
        <text x="42" y="110" fontSize="10" fill="#64748b" fontFamily="IBM Plex Sans">138 kV</text>
        <text x="42" y="125" fontSize="10" fill="#64748b" fontFamily="IBM Plex Sans">Bus 1</text>

        <rect x="270" y="90" width="12" height="80" fill="#0B172A" rx="2" />
        <text x="220" y="110" fontSize="10" fill="#64748b" fontFamily="IBM Plex Sans">13.8 kV</text>
        <text x="226" y="125" fontSize="10" fill="#64748b" fontFamily="IBM Plex Sans">Bus 2</text>

        {/* Transformer (actual) */}
        <line x1="37" y1="125" x2="100" y2="125" stroke="#334155" strokeWidth="2" />
        <circle cx="110" cy="125" r="10" fill="none" stroke="#334155" strokeWidth="2" />
        <circle cx="128" cy="125" r="10" fill="none" stroke="#334155" strokeWidth="2" />
        <line x1="138" y1="125" x2="190" y2="125" stroke="#334155" strokeWidth="2" />
        <text x="100" y="148" fontSize="9" fill="#64748b" fontFamily="IBM Plex Mono">Z_t=17.14 Ω</text>
        <text x="105" y="160" fontSize="8" fill="#94a3b8" fontFamily="IBM Plex Sans">(at 138 kV)</text>

        {/* Line (actual) */}
        <line x1="190" y1="125" x2="270" y2="125" stroke="#334155" strokeWidth="2" strokeDasharray="4 2" />
        <text x="193" y="108" fontSize="9" fill="#64748b" fontFamily="IBM Plex Mono">X=2.0 Ω</text>
        <text x="196" y="120" fontSize="8" fill="#94a3b8" fontFamily="IBM Plex Sans">(13.8kV)</text>

        {/* Per-unit system buses */}
        <rect x="390" y="90" width="12" height="80" fill="#0d9488" rx="2" />
        <text x="407" y="110" fontSize="10" fill="#0d9488" fontFamily="IBM Plex Sans">1.0 pu</text>
        <text x="407" y="125" fontSize="10" fill="#64748b" fontFamily="IBM Plex Sans">Bus 1</text>

        <rect x="638" y="90" width="12" height="80" fill="#0d9488" rx="2" />
        <text x="575" y="110" fontSize="10" fill="#0d9488" fontFamily="IBM Plex Sans">1.0 pu</text>
        <text x="580" y="125" fontSize="10" fill="#64748b" fontFamily="IBM Plex Sans">Bus 2</text>

        {/* Transformer (per-unit) */}
        <line x1="402" y1="125" x2="466" y2="125" stroke="#0d9488" strokeWidth="2" />
        <rect x="466" y="115" width="60" height="20" rx="4" fill="#ccfbf1" stroke="#0d9488" strokeWidth="1.5" />
        <text x="496" y="128" textAnchor="middle" fontSize="9" fill="#0f766e" fontFamily="IBM Plex Mono" fontWeight="700">j0.10 pu</text>
        <line x1="526" y1="125" x2="576" y2="125" stroke="#0d9488" strokeWidth="2" />
        <text x="476" y="148" fontSize="9" fill="#0d9488" fontFamily="IBM Plex Mono">Transformer</text>

        {/* Line (per-unit) */}
        <rect x="576" y="115" width="60" height="20" rx="4" fill="#f0fdfa" stroke="#0d9488" strokeWidth="1.5" strokeDasharray="4 2" />
        <text x="606" y="128" textAnchor="middle" fontSize="9" fill="#0f766e" fontFamily="IBM Plex Mono" fontWeight="700">j1.05 pu</text>
        <line x1="636" y1="125" x2="638" y2="125" stroke="#0d9488" strokeWidth="2" />
        <text x="579" y="148" fontSize="9" fill="#0d9488" fontFamily="IBM Plex Mono">Feeder line</text>

        {/* Arrow between systems */}
        <text x="325" y="118" fontSize="18" fill="#0d9488">⟹</text>
        <text x="315" y="138" fontSize="9" fill="#0d9488" fontFamily="IBM Plex Sans">normalize</text>
      </svg>
    </div>
    <p className="text-xs text-slate-500 dark:text-slate-500 text-center mt-2">
      100 MVA base, V_base = 138 kV (HV side), V_base = 13.8 kV (LV side)
    </p>
  </SectionWrapper>
);

// ─── Section 3: Core Theory ───
const CoreTheory = () => (
  <SectionWrapper number="3" title="Core Theory" id="core-theory">
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">What is a Base?</h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">
          A <em>base</em> is a reference value for a quantity. When we say a voltage is "1.05 per-unit," we mean
          its actual value is 1.05 times the base voltage. You always choose two bases freely:{' '}
          <strong className="text-slate-800 dark:text-slate-200">S<sub>base</sub></strong> (three-phase apparent power)
          and <strong className="text-slate-800 dark:text-slate-200">V<sub>base</sub></strong> (line-to-line voltage).
          All other bases are then derived automatically.
        </p>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">Derived Base Quantities</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-2 px-3 text-slate-600 dark:text-slate-400 font-medium">Quantity</th>
                <th className="text-left py-2 px-3 text-slate-600 dark:text-slate-400 font-medium">Formula</th>
                <th className="text-left py-2 px-3 text-slate-600 dark:text-slate-400 font-medium">Typical Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {[
                ['Z_base (Ω)', 'V²_base / S_base', '190.44 Ω at 138 kV, 100 MVA'],
                ['I_base (A)', 'S_base / (√3 × V_base)', '418.4 A at 138 kV, 100 MVA'],
                ['V_base, pu (pu)', '1.0 pu (by definition)', '—'],
                ['S_base, pu (pu)', '1.0 pu (by definition)', '—'],
              ].map(([q, f, v]) => (
                <tr key={q} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="py-2 px-3 font-mono text-xs text-teal-700 dark:text-teal-400">{q}</td>
                  <td className="py-2 px-3 font-mono text-xs text-slate-700 dark:text-slate-300">{f}</td>
                  <td className="py-2 px-3 text-xs text-slate-500 dark:text-slate-500">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">
          Per-Unit Invariance Across Transformers
        </h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">
          This is the key insight of per-unit: if you choose V<sub>base</sub> on each side to match the transformer's
          turns ratio, then the transformer's per-unit impedance is <em>identical</em> whether computed from the
          primary or secondary side. Z<sub>base</sub> scales with V²/S — exactly the same way impedances are referred
          through a transformer (Z' = Z × (N₁/N₂)²). The two effects cancel perfectly, making per-unit impedance
          voltage-side independent.
        </p>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">
          Changing Bases
        </h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">
          Equipment datasheets specify %Z on the manufacturer's nameplate base. Your system study uses a different base.
          The base-change formula converts per-unit impedance from any old base to any new base in one step.
          The S<sub>base</sub> ratio scales linearly; the voltage ratio enters as a square because impedance scales
          with V².
        </p>
      </div>
    </div>
  </SectionWrapper>
);

// ─── Section 4: Formula Sheet ───
const FormulaSheet = () => (
  <SectionWrapper number="4" title="Formula Sheet" id="formulas" alt>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormulaCard
        title="Base Impedance"
        formula={"Z_{base} = \\dfrac{V_{base}^2}{S_{base}}"}
        variables={[
          { sym: 'Z_base', desc: 'Base impedance (Ω)' },
          { sym: 'V_base', desc: 'Line-to-line base voltage (kV or V)' },
          { sym: 'S_base', desc: 'Three-phase base power (MVA or VA)' },
        ]}
        note="Units must be consistent: if V is in kV and S in MVA, result is in Ω."
      />
      <FormulaCard
        title="Base Current"
        formula={"I_{base} = \\dfrac{S_{base}}{\\sqrt{3} \\cdot V_{base}}"}
        variables={[
          { sym: 'I_base', desc: 'Base current (A or kA)' },
          { sym: 'S_base', desc: 'Three-phase apparent power (VA or MVA)' },
          { sym: 'V_base', desc: 'Line-to-line voltage (V or kV)' },
        ]}
      />
      <FormulaCard
        title="Per-Unit Impedance"
        formula={"Z_{pu} = \\dfrac{Z_{actual}}{Z_{base}}"}
        variables={[
          { sym: 'Z_pu', desc: 'Per-unit impedance (dimensionless)' },
          { sym: 'Z_actual', desc: 'Actual impedance in ohms (Ω)' },
          { sym: 'Z_base', desc: 'Base impedance in ohms (Ω)' },
        ]}
      />
      <FormulaCard
        title="Per-Unit Voltage"
        formula={"V_{pu} = \\dfrac{V_{actual}}{V_{base}}"}
        variables={[
          { sym: 'V_pu', desc: 'Per-unit voltage (e.g. 1.02 pu)' },
          { sym: 'V_actual', desc: 'Actual bus voltage (kV)' },
          { sym: 'V_base', desc: 'Chosen base voltage at this bus (kV)' },
        ]}
      />
      <div className="sm:col-span-2">
        <FormulaCard
          title="Base Change Formula"
          formula={"Z_{pu,new} = Z_{pu,old} \\cdot \\dfrac{S_{base,new}}{S_{base,old}} \\cdot \\left(\\dfrac{V_{base,old}}{V_{base,new}}\\right)^{\\!2}"}
          variables={[
            { sym: 'Z_pu,new', desc: 'Per-unit impedance on the new (system) base' },
            { sym: 'Z_pu,old', desc: 'Per-unit impedance on the old (nameplate) base' },
            { sym: 'S_new/S_old', desc: 'Ratio of new to old MVA base' },
            { sym: '(V_old/V_new)²', desc: 'Square of old-to-new voltage base ratio' },
          ]}
          note="Use this formula any time equipment nameplate bases differ from your system study base."
        />
      </div>
    </div>
  </SectionWrapper>
);

// ─── Section 5: Common Mistakes ───
const MISTAKES = [
  {
    title: 'Using line-to-neutral instead of line-to-line for V_base',
    body: 'The per-unit formula uses the line-to-line voltage base throughout three-phase analysis — not line-to-neutral. Z_base = V_LL² / S₃φ. Using V_LN will give a result that is 3× too small. Keep V_base as line-to-line and the formula works directly for three-phase base impedance.',
  },
  {
    title: 'Forgetting to change base when mixing equipment data',
    body: 'A 30 MVA transformer at 8% and a 50 MVA transformer at 6% cannot be compared until both are converted to the same system base. Engineers skip this step under time pressure and get wildly wrong fault currents. Always run the base-change formula before adding impedances.',
  },
  {
    title: 'Mixing per-unit and percent impedance',
    body: 'Nameplate %Z = 8 means Z_pu = 0.08, NOT 8. A common mistake is entering the percent value directly into a per-unit formula. Whenever a nameplate says "%Z = 8%," divide by 100 before using it in any per-unit equation.',
  },
  {
    title: 'Choosing a V_base that does not match the transformer turns ratio',
    body: 'If your 138/13.8 kV transformer has a 10:1 turns ratio and you choose V_base = 138 kV on the HV side, you must choose V_base = 13.8 kV on the LV side. Choosing 12.47 kV on the LV side (a common distribution voltage) introduces an error because the base voltages no longer match the actual turns ratio.',
  },
  {
    title: 'Applying per-unit fault current directly as amperes',
    body: "A fault current of I_F = 3.5 pu means 3.5 times the base current — not 3.5 amperes. Multiply by I_base to recover actual amperes. Forgetting this step is how engineers compare a relay's current setting to a calculated fault current and get the comparison completely wrong.",
  },
];

const CommonMistakes = () => (
  <SectionWrapper number="5" title="Common Mistakes" id="common-mistakes">
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
      These are the five most frequent errors engineers make when working with per-unit analysis —
      sourced from real distribution studies and PE exam postmortems.
    </p>
    <Accordion type="single" collapsible className="space-y-2" data-testid="mistakes-accordion">
      {MISTAKES.map((m, i) => (
        <AccordionItem
          key={i}
          value={`mistake-${i}`}
          className="border border-slate-200 dark:border-slate-700 rounded-lg px-4 bg-white dark:bg-slate-900"
        >
          <AccordionTrigger className="text-sm font-medium text-slate-800 dark:text-slate-200 hover:no-underline hover:text-teal-700 dark:hover:text-teal-400 py-3">
            <span className="flex items-center gap-2.5">
              <span className="text-red-500 text-xs font-bold w-4 flex-shrink-0">#{i + 1}</span>
              {m.title}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed pb-2">{m.body}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </SectionWrapper>
);

// ─── Section 6: Worked Example ───
const STEPS = [
  {
    label: 'Define System Base',
    formula: null,
    text: 'Choose: S_base = 100 MVA, V_base,HV = 138 kV (HV side). This is the given in most system studies — the transformer kV rating matches our base exactly, so no voltage-zone correction is needed.',
    result: null,
  },
  {
    label: 'Set LV Zone Base Voltage',
    formula: null,
    text: 'The transformer turns ratio is 138/13.8 = 10:1. Therefore V_base,LV = 138 / 10 = 13.8 kV. This matches the nominal feeder voltage. Bases are set for both zones.',
    result: null,
  },
  {
    label: 'Compute Z_base on HV Side',
    formula: "Z_{base,HV} = \\dfrac{V_{base,HV}^2}{S_{base}} = \\dfrac{(138)^2}{100} = 190.44 \\, \\Omega",
    text: null,
    result: 'Z_base,HV = 190.44 Ω',
  },
  {
    label: 'Compute Z_base on LV Side',
    formula: "Z_{base,LV} = \\dfrac{V_{base,LV}^2}{S_{base}} = \\dfrac{(13.8)^2}{100} = 1.904 \\, \\Omega",
    text: null,
    result: 'Z_base,LV = 1.904 Ω',
  },
  {
    label: 'Per-Unit the Transformer',
    formula: "X_{t,pu} = \\dfrac{X_t}{Z_{base,HV}} = \\dfrac{\\%Z}{100} = \\dfrac{8}{100} = j0.080 \\, \\text{pu}",
    text: 'The transformer nameplate is on a 100 MVA, 138 kV base — same as our system base. No base change needed. The 8% nameplate value converts directly to 0.080 pu.',
    result: 'X_t = j0.080 pu',
  },
  {
    label: 'Per-Unit the Feeder Line',
    formula: "X_{line,pu} = \\dfrac{X_{line}}{Z_{base,LV}} = \\dfrac{2.0}{1.904} = j1.050 \\, \\text{pu}",
    text: null,
    result: 'X_line = j1.050 pu',
  },
  {
    label: 'Total Series Impedance',
    formula: "Z_{total,pu} = j0.080 + j1.050 = j1.130 \\, \\text{pu}",
    text: 'Both elements are now in the same per-unit system and can simply be added (series connection).',
    result: 'Z_total = j1.130 pu',
  },
  {
    label: 'Interpret: Bolted Three-Phase Fault Current',
    formula: "I_F = \\dfrac{V_F}{Z_{total}} = \\dfrac{1.0}{1.130} = 0.885 \\, \\text{pu}",
    text: 'A prefault voltage of 1.0 pu is standard. The resulting fault current is 0.885 pu. To convert to amperes: multiply by I_base = S_base / (√3 × V_base,LV) = 100,000 / (1.732 × 13.8) = 4,183 A. So I_F = 0.885 × 4,183 = 3,702 A.',
    result: 'I_F ≈ 3,702 A at the feeder end',
  },
];

const WorkedExample = () => (
  <SectionWrapper number="6" title="Step-by-Step Worked Example" id="worked-example" alt>
    <div className="mb-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest mb-3">Problem Statement</p>
      <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
        A <strong>100 MVA, 138/13.8 kV transformer</strong> has a nameplate impedance of <strong>8%</strong>.
        A <strong>13.8 kV feeder line</strong> has a reactance of <strong>X = 2.0 Ω</strong>.
        Find all per-unit values on a <strong>100 MVA, 138 kV system base</strong>.
        Calculate the fault current at the end of the feeder for a bolted three-phase fault.
      </p>
    </div>

    <div className="space-y-3" data-testid="worked-example-steps">
      {STEPS.map((step, i) => (
        <div
          key={i}
          className="step-card border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900"
        >
          <div className="flex items-start gap-3">
            <div className="section-badge flex-shrink-0 text-xs">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 dark:text-white text-sm mb-2 font-heading">{step.label}</p>
              {step.text && (
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-2">{step.text}</p>
              )}
              {step.formula && (
                <div className="overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3 my-2">
                  <BlockMath math={step.formula} />
                </div>
              )}
              {step.result && (
                <div className="inline-flex items-center gap-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-md px-3 py-1.5 mt-1">
                  <ChevronRight className="h-3 w-3 text-teal-600" />
                  <span className="text-xs font-mono font-semibold text-teal-800 dark:text-teal-300">{step.result}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

// ─── Section 7: Interactive Calculators ───
const CalculatorsSection = () => (
  <SectionWrapper number="7" title="Interactive Calculators" id="calculators">
    <Tabs defaultValue="base" className="w-full" data-testid="calculators-tabs">
      <TabsList className="mb-6 bg-slate-100 dark:bg-slate-800">
        <TabsTrigger value="base" data-testid="tab-base">Base Calculator</TabsTrigger>
        <TabsTrigger value="change" data-testid="tab-change">Base Change</TabsTrigger>
        <TabsTrigger value="network" data-testid="tab-network">Network Builder</TabsTrigger>
      </TabsList>

      <TabsContent value="base">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white font-heading mb-1">Per-Unit Base Calculator</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Enter S<sub>base</sub> and V<sub>base</sub> to compute Z<sub>base</sub> and I<sub>base</sub>.
            The chart shows how Z<sub>base</sub> changes with voltage — observe the quadratic V² relationship.
          </p>
        </div>
        <PerUnitBaseCalc />
      </TabsContent>

      <TabsContent value="change">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white font-heading mb-1">Base Change Calculator</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Convert equipment nameplate per-unit impedance to your system base using the base-change formula.
            Enter the old (nameplate) base, new (system) base, and the original Z<sub>pu</sub>.
          </p>
        </div>
        <BaseChangeCalc />
      </TabsContent>

      <TabsContent value="network">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white font-heading mb-1">Per-Unit Network Builder</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Add transformers and lines to a series network. Enter actual impedances and voltage bases —
            the builder converts each element to per-unit and sums the total series impedance.
          </p>
        </div>
        <NetworkBuilder />
      </TabsContent>
    </Tabs>
  </SectionWrapper>
);

// ─── Section 8: Self-Check Quiz ───
const QUESTIONS = [
  {
    q: 'Given S_base = 50 MVA and V_base = 13.8 kV (L-L), what is Z_base?',
    options: [
      { k: 'a', label: '1.904 Ω' },
      { k: 'b', label: '3.808 Ω' },
      { k: 'c', label: '7.616 Ω' },
      { k: 'd', label: '9.524 Ω' },
    ],
    correct: 'b',
    explanation: 'Z_base = V² / S = 13.8² / 50 = 190.44 / 50 = 3.808 Ω. Remember: units — if V is in kV and S in MVA, the result is directly in Ω.',
  },
  {
    q: 'A transformer nameplate says Z = 8% on a 30 MVA, 13.8 kV base. What is Z_pu on a 100 MVA, 13.8 kV system base?',
    options: [
      { k: 'a', label: '0.024 pu' },
      { k: 'b', label: '0.080 pu' },
      { k: 'c', label: '0.267 pu' },
      { k: 'd', label: '0.800 pu' },
    ],
    correct: 'c',
    explanation: 'Z_pu,new = 0.08 × (100/30) × (13.8/13.8)² = 0.08 × 3.333 × 1 = 0.267 pu. The voltage bases are equal so only the S_base ratio matters here.',
  },
  {
    q: 'If you double V_base while keeping S_base constant, what happens to Z_base?',
    options: [
      { k: 'a', label: 'Doubles (2×)' },
      { k: 'b', label: 'Stays the same' },
      { k: 'c', label: 'Halves (0.5×)' },
      { k: 'd', label: 'Quadruples (4×)' },
    ],
    correct: 'd',
    explanation: 'Z_base = V² / S. Doubling V → Z_base = (2V)² / S = 4V² / S = 4 × Z_base. The quadratic relationship means voltage changes have a magnified effect on Z_base.',
  },
  {
    q: 'A feeder resistance is R = 1.5 Ω actual. The system base impedance is Z_base = 5.0 Ω. What is R_pu?',
    options: [
      { k: 'a', label: '0.03 pu' },
      { k: 'b', label: '0.15 pu' },
      { k: 'c', label: '0.30 pu' },
      { k: 'd', label: '3.00 pu' },
    ],
    correct: 'c',
    explanation: 'R_pu = R_actual / Z_base = 1.5 / 5.0 = 0.30 pu. Note: for impedance, always use Z_base — not I_base or S_base.',
  },
  {
    q: 'Why does a transformer\'s per-unit impedance stay the same on both sides (when bases are correctly chosen)?',
    options: [
      { k: 'a', label: 'Because the transformer is lossless (ideal)' },
      { k: 'b', label: 'Z_base = V²/S scales with the turns ratio exactly as impedance is referred — the two effects cancel' },
      { k: 'c', label: 'Only true for wye-connected transformers' },
      { k: 'd', label: 'It doesn\'t — you must recalculate for each side' },
    ],
    correct: 'b',
    explanation: "Referring Z across a transformer multiplies by (N₁/N₂)². Z_base on the other side also changes by (V₁/V₂)² = (N₁/N₂)². So Z_pu = Z_actual/Z_base — both numerator and denominator scale by the same turns-ratio factor, and the per-unit value is unchanged. This is per-unit's most powerful property.",
  },
];

const SelfCheckQuiz = () => {
  const [selected, setSelected] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = submitted
    ? QUESTIONS.filter((q, i) => selected[i] === q.correct).length
    : 0;

  const reset = () => { setSelected({}); setSubmitted(false); };

  return (
    <SectionWrapper number="8" title="Self-Check Quiz" id="quiz" alt>
      <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
        Five multiple-choice questions to test your per-unit understanding. Select an answer for each, then click Submit.
      </p>
      <div className="space-y-6" data-testid="quiz-section">
        {QUESTIONS.map((q, qi) => (
          <div key={qi} data-testid={`quiz-question-${qi}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
            <p className="font-medium text-slate-900 dark:text-white text-sm mb-3">
              <span className="text-teal-700 dark:text-teal-400 font-mono mr-2">Q{qi + 1}.</span>
              {q.q}
            </p>
            <div className="space-y-2">
              {q.options.map(opt => {
                const isSelected = selected[qi] === opt.k;
                const isCorrect = opt.k === q.correct;
                let cls = 'border rounded-md px-3 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2 ';
                if (!submitted) {
                  cls += isSelected
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-900 dark:text-teal-100'
                    : 'border-slate-200 dark:border-slate-700 hover:border-teal-400 text-slate-700 dark:text-slate-300';
                } else {
                  if (isCorrect) cls += 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100';
                  else if (isSelected) cls += 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100';
                  else cls += 'border-slate-100 dark:border-slate-800 text-slate-400 dark:text-slate-600 opacity-50';
                }
                return (
                  <button
                    key={opt.k}
                    data-testid={`quiz-option-${qi}-${opt.k}`}
                    className={cls}
                    onClick={() => !submitted && setSelected(prev => ({ ...prev, [qi]: opt.k }))}
                    disabled={submitted}
                  >
                    {submitted && isCorrect && <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />}
                    {submitted && isSelected && !isCorrect && <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                    <span className="font-mono text-xs font-bold mr-1">{opt.k.toUpperCase()}.</span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {submitted && (
              <div className="mt-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3">
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  <span className="font-semibold text-teal-700 dark:text-teal-400">Explanation: </span>
                  {q.explanation}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-4 mt-6">
        {!submitted ? (
          <button
            data-testid="quiz-submit"
            onClick={() => Object.keys(selected).length > 0 && setSubmitted(true)}
            disabled={Object.keys(selected).length === 0}
            className="px-5 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors text-sm"
          >
            Submit Answers ({Object.keys(selected).length}/{QUESTIONS.length} answered)
          </button>
        ) : (
          <>
            <div
              data-testid="quiz-score"
              className={`px-4 py-2 rounded-md text-sm font-semibold ${
                score === QUESTIONS.length
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
                  : score >= 3
                  ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                  : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
              }`}
            >
              Score: {score}/{QUESTIONS.length}
              {score === QUESTIONS.length && ' — Perfect!'}
              {score >= 3 && score < QUESTIONS.length && ' — Good work'}
              {score < 3 && ' — Review the formulas and try again'}
            </div>
            <button
              data-testid="quiz-reset"
              onClick={reset}
              className="text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 underline transition-colors"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </SectionWrapper>
  );
};

// ─── Section 9: Connected Topics ───
const ConnectedTopics = () => (
  <SectionWrapper number="9" title="Connected Topics" id="connected-topics">
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
      Per-Unit Analysis sits at the center of the power systems learning chain.
      Review the prerequisites below or continue to the next module.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">Prerequisites</p>
        <div className="space-y-2">
          {[
            { to: '/foundation', label: 'M01 Foundation', desc: 'Three-phase phasors, power equations' },
            { to: '/transformers', label: 'M02 Transformer Systems', desc: 'Turns ratio, leakage impedance, %Z' },
          ].map(({ to, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-teal-400 dark:hover:border-teal-700 transition-colors bg-white dark:bg-slate-900"
            >
              <ArrowLeft className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">Next Steps</p>
        <div className="space-y-2">
          {[
            { to: '/symmetrical-components', label: 'M04 Symmetrical Components', desc: 'Positive, negative, and zero sequence' },
            { to: '/sequence-networks', label: 'M05 Sequence Networks', desc: 'Building zero-sequence models' },
            { to: '/fault-analysis', label: 'M06 Fault Analysis', desc: 'SLG, LL, DLG, and 3-phase faults' },
          ].map(({ to, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 p-3 border border-teal-100 dark:border-teal-900/40 rounded-lg hover:border-teal-400 dark:hover:border-teal-700 transition-colors bg-teal-50/50 dark:bg-teal-900/10"
            >
              <ArrowRight className="h-4 w-4 text-teal-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  </SectionWrapper>
);

// ─── Main Module Page ───
const PerUnitModule = () => (
  <div data-testid="per-unit-module">
    {/* Module Header */}
    <div
      className="relative bg-gradient-to-br from-[#0B172A] via-teal-900 to-slate-800 text-white px-4 sm:px-6 lg:px-8 py-12 overflow-hidden"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1509390221805-d1c887a72a00?w=1200&q=30)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-[#0B172A]/85" />
      <div className="relative max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-5 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Curriculum Map
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-mono font-semibold text-teal-400 bg-teal-400/10 border border-teal-400/20 px-2 py-1 rounded">M03</span>
          <span className="text-xs font-bold bg-teal-500 text-white px-2 py-0.5 rounded-full">LIVE</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold font-heading mb-3 leading-tight">Per-Unit Analysis</h1>
        <p className="text-slate-300 text-lg mb-3">System Normalization — the universal language of fault analysis</p>
        <div className="flex flex-wrap gap-2">
          {['Foundation (M01)', 'Transformer Systems (M02)'].map(p => (
            <span key={p} className="text-xs bg-white/10 border border-white/20 px-2.5 py-1 rounded-full text-slate-300">
              Requires: {p}
            </span>
          ))}
        </div>
      </div>
    </div>

    {/* Section Nav */}
    <div className="sticky top-14 z-20 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800 overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        <div className="flex gap-0 py-0" style={{ scrollbarWidth: 'none' }}>
          {[
            { n: 1, label: 'Why' }, { n: 2, label: 'Visual' }, { n: 3, label: 'Theory' },
            { n: 4, label: 'Formulas' }, { n: 5, label: 'Mistakes' }, { n: 6, label: 'Example' },
            { n: 7, label: 'Calculators' }, { n: 8, label: 'Quiz' }, { n: 9, label: 'Connections' },
          ].map(({ n, label }) => (
            <a
              key={n}
              href={`#${['why-it-matters','concept-visual','core-theory','formulas','common-mistakes','worked-example','calculators','quiz','connected-topics'][n-1]}`}
              className="px-3 py-3 text-xs font-medium text-slate-500 dark:text-slate-500 hover:text-teal-700 dark:hover:text-teal-400 whitespace-nowrap border-b-2 border-transparent hover:border-teal-500 transition-colors flex-shrink-0"
            >
              <span className="text-teal-600 dark:text-teal-500 mr-1 font-mono">{n}.</span>{label}
            </a>
          ))}
        </div>
      </div>
    </div>

    {/* All 9 Sections */}
    <WhyItMatters />
    <ConceptVisual />
    <CoreTheory />
    <FormulaSheet />
    <CommonMistakes />
    <WorkedExample />
    <CalculatorsSection />
    <SelfCheckQuiz />
    <ConnectedTopics />

    {/* Bottom nav */}
    <div className="border-t border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <Link
          to="/transformers"
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          M02 Transformer Systems
        </Link>
        <Link
          to="/symmetrical-components"
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
        >
          M04 Symmetrical Components
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </div>
);

export default PerUnitModule;
